import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { CloseButton } from 'components/common/Toast';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.min.css';
import { useAppContext } from 'Main';
import MainLayout from 'layouts/MainLayout';
import Landing from 'components/pages/landing/Landing';
import Agb from 'components/pages/landing/Agb';
import Datenschutz from 'components/pages/landing/DatenSchutz';
import Impressum from 'components/pages/landing/Impressum';
import is from 'is_js';
import Error404 from 'components/errors/Error404';
import ScrollToTop from 'components/common/ScrollTop';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Lizenz from 'components/pages/landing/Lizenz';
import ReleaseNotes from 'components/pages/landing/ReleaseNotes';

const App = () => {
  const HTMLClassList = document.getElementsByTagName('html')[0].classList;
  const {
    config: { navbarPosition },
  } = useAppContext();

  useEffect(() => {
    if (is.windows()) {
      HTMLClassList.add('windows');
    }
    if (is.chrome()) {
      HTMLClassList.add('chrome');
    }
    if (is.firefox()) {
      HTMLClassList.add('firefox');
    }
    if (is.safari()) {
      HTMLClassList.add('safari');
    }
  }, [HTMLClassList]);

  useEffect(() => {
    if (navbarPosition === 'double-top') {
      HTMLClassList.add('double-top-nav-layout');
    }
    return () => HTMLClassList.remove('double-top-nav-layout');
  }, [navbarPosition]);

  return (
    <HelmetProvider>
      <Helmet
        defaultTitle="Scanbox – System zur Angriffserkennung"
        titleTemplate="%s | Scanbox"
      >
        <html lang="de" />
        <meta
          name="description"
          content="Scanbox ist ein benutzerfreundliches System zur Angriffserkennung, das IT-Administratoren bei der effizienten Sicherheitsanalyse unterstützt."
        />
        <link rel="canonical" href="https://www.scanbox-product.de/" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Scanbox" />
        <meta property="og:locale" content="de_DE" />
        <meta
          property="og:title"
          content="Scanbox – System zur Angriffserkennung"
        />
        <meta
          property="og:description"
          content="Ein benutzerfreundliches System zur Angriffserkennung, das IT-Administratoren bei der effizienten Sicherheitsanalyse unterstützt."
        />
        <meta property="og:url" content="https://www.scanbox-product.de/" />
        <meta
          property="og:image"
          content="https://www.scanbox-product.de/og-image.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Scanbox – System zur Angriffserkennung"
        />
        <meta
          name="twitter:description"
          content="Ein benutzerfreundliches System zur Angriffserkennung, das IT-Administratoren bei der effizienten Sicherheitsanalyse unterstützt."
        />
        <meta
          name="twitter:image"
          content="https://www.scanbox-product.de/og-image.png"
        />

        {/* Structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Scanbox',
            url: 'https://www.scanbox-product.de/',
            logo: 'https://www.scanbox-product.de/og-image.png',
            description:
              'Scanbox ist ein System zur Angriffserkennung, das IT-Administratoren bei der effizienten Sicherheitsanalyse unterstützt.',
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Scanbox',
            applicationCategory: 'SecurityApplication',
            operatingSystem: 'Web',
            url: 'https://www.scanbox-product.de/',
            description:
              'Benutzerfreundliches System zur Angriffserkennung und Sicherheitsanalyse für IT-Administratoren.',
          })}
        </script>
      </Helmet>

      <Router basename={process.env.PUBLIC_URL}>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<MainLayout />}>
            <Route index element={<Landing />} />
            <Route path="Agb" element={<Agb />} />
            <Route path="Datenschutz" element={<Datenschutz />} />
            <Route path="Lizenz" element={<Lizenz />} />
            <Route path="ReleaseNotes" element={<ReleaseNotes />} />
            <Route path="Impressum" element={<Impressum />} />
            <Route path="*" element={<Error404 />} />
          </Route>
        </Routes>
        <ToastContainer
          closeButton={CloseButton}
          icon={false}
          position={toast.POSITION.TOP_RIGHT}
        />
      </Router>
    </HelmetProvider>
  );
};

export default App;
